import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AssistSurfaceController } from './AssistSurfaceController';
import { analyticsFiltersEqual } from '@/shared/analytics/AnalyticsFilterOps';
import { decodePageFilters, encodePageFilters } from './PageFilterUrlCodec';

/**
 * Mirrors the analytics page-tier filters into the URL (`pf`, surface-tagged) so
 * they survive reload and deep-link, alongside the global tier. The singleton
 * {@link AssistSurfaceController} stays the source of truth (drill-in persistence
 * is in-memory); this only hydrates from and writes to the URL. Must be invoked
 * after `setSurface`, so a surface change clears before the URL re-hydrates.
 */
export function usePageFilterUrlSync(controller: AssistSurfaceController, surfaceId: string): void {
    const [params, setParams] = useSearchParams();

    useEffect(() => {
        const fromUrl = decodePageFilters(params, surfaceId);
        if (fromUrl !== null && !analyticsFiltersEqual(fromUrl, controller.activeFilters)) {
            controller.replaceAll(fromUrl);
        }
    }, [params, surfaceId, controller]);

    useEffect(() => {
        return controller.subscribe(() => {
            setParams((prev) => {
                const next = encodePageFilters(surfaceId, controller.activeFilters, prev);
                return next.toString() === prev.toString() ? prev : next;
            }, { replace: true });
        });
    }, [controller, surfaceId, setParams]);
}
